<template>
  <div>
    <ValidationObserver
      ref="validationobserver"
      v-slot="{ handleSubmit }"
      class="mx-3"
    >
      <b-form
        @submit.prevent="handleSubmit(login)"
        class="d-flex flex-column justify-content-center align-items-center"
      >
        <div
          class="loginForm mw-100 d-flex flex-column justify-content-center align-items-center"
        >
          <DefaultFormValidationProvider
            label="Usuário (CPF)"
            v-slot="defaultState"
            class="w-100"
          >
            <b-form-input
              id="input-usuario"
              :value="usuarioFormatted"
              @input="onUsuarioInput"
              @keydown="onUsuarioKeydown"
              :state="defaultState.state"
              autofocus
              autocomplete="username"
            />
          </DefaultFormValidationProvider>
          <DefaultFormValidationProvider
            label="Senha"
            v-slot="defaultState"
            class="w-100"
          >
            <b-form-input
              id="input-senha"
              v-model="senha"
              :state="defaultState.state"
              type="password"
              autocomplete="current-password"
            />
          </DefaultFormValidationProvider>
        </div>
        <b-row>
          <b-col cols="12" class="d-flex justify-content-center">
            <b-button
              class="m-1"
              :variant="isDev ? 'secondary' : isHml ? 'primary' : 'success'"
              size="sm"
              type="submit"
            >
              <font-awesome-icon icon="keyboard" class="mr-2" />
              Entrar
            </b-button>
            <b-button
              variant="outline-secondary"
              @click="modalShow = !modalShow"
              class="m-1"
              size="sm"
            >
              <font-awesome-icon icon="lock" class="mr-2" />
              Esqueci a senha
            </b-button>
          </b-col>
        </b-row>
      </b-form>
    </ValidationObserver>

    <ModalTrocaSenha
      v-if="modalShow"
      :show="modalShow"
      :cpfDigits="cpfDigits"
      :loadingEmail="loadingEmail"
      :variant="isDev ? 'secondary' : isHml ? 'primary' : 'success'"
      @update:cpfDigits="cpfDigits = $event"
      @submit="enviarEmail"
      @close="modalShow = false"
    />
  </div>
</template>

<script lang="ts">
import { isDev, isHml, store } from "@/store";
import { UtilsTS } from "@/utilsts";
import { ValidationObserver } from "vee-validate";
import DefaultFormValidationProvider from "@/components/shared/DefaultFormValidationProvider.vue";
import { mixins } from "vue-class-component";
import { Component, Ref, Vue } from "vue-property-decorator";
import usuarioService from "@/services/administracao/usuarioService";
import ModalTrocaSenha from "@/components/login/ModalTrocaSenha.vue";

const CompProps = Vue.extend({
  name: "LoginForm",
  components: {
    DefaultFormValidationProvider,
    ModalTrocaSenha,
    ValidationObserver,
  },
});

@Component
export default class LoginForm extends mixins(UtilsTS, CompProps) {
  @Ref("validationobserver") validationobserver: InstanceType<
    typeof ValidationObserver
  >;
  senha = "";
  usuario = "";
  usuarioDigits = "";
  cpfDigits = "";
  loadingEmail = false;
  cpf = "";
  modalShow = false;
  isDev = isDev();
  isHml = isHml();

  get usuarioFormatted(): string {
    const d = (this.usuarioDigits || "").slice(0, 11);
    if (d.length <= 3) return d;
    if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
    if (d.length <= 9)
      return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
    return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
  }

  onUsuarioInput(value: string): void {
    const digits = (value || "").replace(/\D+/g, "").slice(0, 11);
    this.usuarioDigits = digits;
    this.usuario = digits;
  }

  onUsuarioKeydown(e: KeyboardEvent): void {
    const allowed = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Home", "End", "Tab"];
    if (e.ctrlKey || e.metaKey) return;
    if (allowed.includes(e.key)) return;
    if (!/^\d$/.test(e.key)) e.preventDefault();
  }

  get cpfFormatted(): string {
  const d = (this.cpfDigits || "").slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
  if (d.length <= 9)
    return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
}

  onCpfInput(value: string): void {
    this.cpfDigits = (value || "").replace(/\D+/g, "").slice(0, 11);
  }

  onCpfKeydown(e: KeyboardEvent): void {
    const allowed = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Home", "End", "Tab"];
    if (e.ctrlKey || e.metaKey) return;
    if (allowed.includes(e.key)) return;
    if (!/^\d$/.test(e.key)) e.preventDefault();
  }

  async login(): Promise<void> {
    store.blockScreen = true;
    try {
      const isValid = await this.validationobserver.validate();
      if (!isValid) console.error(this.validationobserver);
      else {
      const cpf = (this.usuario || "").replace(/\D+/g, "").padStart(11, "0");
      const ld = await usuarioService.login(cpf, this.senha);
        this.$emit("dologin", ld);
      }
    } catch (e: any) {
      this.tratarError(e);
    } finally {
      store.blockScreen = false;
    }
  }

  async enviarEmail() {
    try {
      this.loadingEmail = true;

      const cpf = this.cpfDigits.padStart(11, "0");
      if (cpf.length === 11) {
        const response = await usuarioService.enviarEmailTrocaSenha(cpf);
        this.$toast.success(
          "Email enviado com sucesso! Acesse o link de acesso na sua caixa de entrada"
        );
        this.modalShow = false;
      } else {
        this.$toast.error("Insira seu cpf.");
      }
    } catch (e: any) {
      this.tratarError(e);
    } finally {
      this.loadingEmail = false;
    }
  }
}
</script>

<style scoped>
div.loginForm {
  width: 25rem;
  font-family: "Montserrat", sans-serif;
}
</style>
